//=============================================
// ARCHELIOS MAP NAMESPACE
//=============================================
var am = (function(module) {

    //Use strict mode
    'use strict';
    
    /**
     * @class am.AddressSearchBarController
     * @fires AddressSearchBarController#new_address_entered
     * listens event:expand_address_search_field
     * listens event:collapse_address_search_field
     *
     * This class implements the controller of the address search bar. Beware that this class REQUIRES the "Google 
     * search places" to have been loaded.
     */
    module.AddressSearchBarController = function() {
        // Private attributes
        this._view = undefined; // reference to the view associated with the controller
        this._map  = undefined; // reference to the map
        this._oGeocoder = undefined;      // instance of the Google class searching for addresses
        this._autocompleter = undefined; // instance of the Google class performing address autocompletion
        this._oCustomAddressQuery = undefined;
        this._newAddressCallback = undefined; // variable storing the callback to be executed each time a new address is entered by the user
        this._config = {
            position: 'topleft',      // position of the widget relative to the map container
            searchBounds: undefined,  // geographical area over which the address search should be restricted
            searchCountry: undefined  // country over which the address search should be restricted (e.g. 'fr', 'en', etc.)
        };
        this._projectBounds = undefined; // object holding the lat/lng bounds of the current projet (in WGS84 coordinates)
    };

    /**
     * Class constructor
     * @constructor
     *
     * @param {Object} mainController - Reference to the main controller of the application
     * @param {Object} config - Object specifying the configuration to be applied to the controller. This object can
     *      define the same parameters as argument 'options' in the constructor of class L.Control 
     *      (see http://leafletjs.com/reference-1.2.0.html#control). The following parameters can additionally be 
     *      specified:
     *          - searchBounds: in case this parameter is specified, the address search will be restricted to the 
     *              specified geographical bounds. The parameter should be specified as a L.LatLngBounds intance.
     *              Alternatively, the it can take the value 'inherit', in which case the address search will be 
     *              restricted to the geographical extents of the map as the widget is added to the map.
     *              Defaults to undefined, i.e. no geographical restriction. Notice that, according to the Google API 
     *              documentation, the search will not be strictly restricted to the specified bounds (the bounds will
     *              rather be regarded as a suggested area in which the address should be preferentially be searched for).
     *          - searchCountry: country in which the address should be searched for. This parameter should be specified
     *              as a 2-letter code according to the ISO 3166-1 convention (e.g. 'FR' for France). Defaults to 
     *              undefined, i.e. the address search is performed over the entire world.
     */
    module.AddressSearchBarController.prototype.init = function(mainController, config) {
        var _this = this;
        this._map = mainController.getMapObject();

        // Merge the config specified by the user with the default config
        this._config = am.utils.mergeObjects(this._config, config);
        if (this._config.searchBounds == 'inherit') {
            this._config.searchBounds = this._map.getBounds();
        }

        // Instantiate the private attributes, except for the autocomplete object and its associated event listener object
        this._view = am.utils.addressSearchBarView(this._config).addTo(this._map);
        this._oGeocoder = new am.utils.customGeocoder("Google");
        this._newAddressCallback = function(event) {
            _this._sendNewAddressEvent(event);
        };
        var projectBounds = mainController.getConfig().mapOptions.maxBounds;
        if (projectBounds) {
            this._projectBounds = projectBounds;
        }

        // Instantiate the autocomplete object and its associated event listener object
        this._view.getInputField().then(function(inputField) {
            var autocompObj = new am.utils.addressAutoCompleter(_this._oGeocoder, inputField, _this._getGeocodingOptions(_this._oGeocoder.getType()));
            autocompObj.addListener(_this._newAddressCallback);
            _this._autocompleter = autocompObj;
            if(am.utils.addressCustomQuery !== undefined){
	          _this._oCustomAddressQuery = new am.utils.addressCustomQuery(mainController, _this, inputField);
	        }
        });

        // Define the action to undertake when the 'Search' button is pressed
        this._view.getForm().then(function(formElement) {
            var $formElement = $(formElement);
            $formElement.on('submit', _this._newAddressCallback);
            $formElement.find('#am-address-clear-button').on('click tap', function() {
                $('#am-address-search-field').focus();
                _this._view.clearSearchField();
            });
        });

        // Hack: prevent submitting form when hitting Enter while focus is on the input field. This is required since the
        // 'form submit' event is fired before the Google API copies the user selection into the input field
        this._view.getInputField().then(function(inputField) {
            $(inputField).on('keyup keypress', function(event) {
                _this._handleKeyPressEvent(event);
            });
        });

        // Attach event handlers to the present class
        am.eventManager.register('expand_address_search_field', this._view.expandSearchField, this._view);
        am.eventManager.register('collapse_address_search_field', this._view.collapseSearchField, this._view);
        am.eventManager.register('hide_address_search_field', this._view.hideSearchField, this._view);
        am.eventManager.register('show_address_search_field', this._view.showSearchField, this._view);
        am.eventManager.register('display_address_of_location', this._displayAddressOfLocation, this);
        am.eventManager.register('clear_search_field', this._view.clearSearchField, this._view);
    };

    /**
     * Class destructor
     */
    module.AddressSearchBarController.prototype.destroy = function() {
        if(this._autocompleter){
          this._autocompleter.removeListener();
        }
        this._view.getForm().then(function(formElement) {
            $(formElement).off('submit', _this._newAddressCallback);
        });
        this._view.getInputField().then(function(inputField) {
            $(inputField).off('keyup keypress', _this._handleKeyPressEvent);
        });
        am.eventManager.unregister('expand_address_search_field', this._view.expandSearchField, this._view);
        am.eventManager.unregister('collapse_address_search_field', this._view.collapseSearchField, this._view);
        am.eventManager.unregister('hide_address_search_field', this._view.hideSearchField, this._view);
        am.eventManager.unregister('show_address_search_field', this._view.showSearchField, this._view);
        am.eventManager.unregister('display_address_of_location', this._displayAddressOfLocation, this);
        am.eventManager.unregister('clear_search_field', this._view.clearSearchField, this._view);

        // Remove panel from the map and remove references to external objects
        this._view.remove();
        this._map = undefined;
    };

    /**
     * Private function sending a 'new-address-entered' event in case the address entered by the user in the input field
     * is valid.
     * @fires AddressSearchBarController#new_address_entered
     *
     * @param {Event} [triggeringEvent] - Optional parameter corresponding to the event which caused the present 
     *      function to be called, if any. If this argument is specified, the propagation of the event will be blocked.
     */
    module.AddressSearchBarController.prototype._sendNewAddressEvent = function(triggeringEvent) {
        // Prevent propagation of the triggering event, if any
        if (triggeringEvent) {
            triggeringEvent.preventDefault();
            triggeringEvent.stopPropagation();
        }

        // Hack: autocomplete.getPlace() does not always return the text which is in the input field. We therefore read
        // the input field and ask Google to find the place corresponding to this text.
        var self = this;
        this._view.getInputField().then(function(inputField) {
            if (inputField.value) {
              var _fnGeocodeAddress = function(sAddress){
                self._oGeocoder.geocode(self._getGeocodingOptions(self._oGeocoder.getType(), sAddress), function (results, status) { // caution: results can be null if status == 'OVER_QUERY_LIMIT'
                  if (status == "OK" && results[0].types.indexOf('country') == -1) { // hack: in case a country was specified to restrict the search, Google returns this country if no matching address was found
                    var result = results[0];
                    inputField.value = result.formatted_address;

                    var location = L.latLng(result.geometry.location.lat(), result.geometry.location.lng());
                    if (self._projectBounds && !self._projectBounds.contains(location)) {
                      self._displayErrorMessage(inputField, "addressOutsideBounds");
                      return;
                    }

                    var bounds = result.geometry.bounds;
                    if (bounds) {
                      var llCorner = bounds.getSouthWest();
                      var urCorner = bounds.getNorthEast();
                      bounds = L.latLngBounds(
                        L.latLng(llCorner.lat(), llCorner.lng()),
                        L.latLng(urCorner.lat(), urCorner.lng())
                      );
                    }

                    am.eventManager.trigger('new_address_entered', {
                      location: location,
                      bounds: bounds
                    });
                  } else if (status == "ZERO_RESULTS" || results[0].types.indexOf('country') != -1) {
                    self._displayErrorMessage(inputField, "unknownAddress");
                  } else {
                    self._displayErrorMessage(inputField, "trySearchingAgain");
                  }
                });
              };

              if(self._oCustomAddressQuery){
                var sQuery = inputField.value;
                self._oCustomAddressQuery.search(sQuery, function(oMatch, sStatus){
                  var sAddress = "NONE";
                  if(oMatch && sStatus==="OK"){
                    sAddress = oMatch.formatted_address;
                  }
                  return _fnGeocodeAddress(sAddress);
                });
              }
              else {
                var sAddress = inputField.value;
                return _fnGeocodeAddress(sAddress);
              }
            }
        });
    };

    /**
     * Private function used to prevent the propagation of the event passed as input argument if this event was emitted 
     * when the user pressed on Enter
     *
     * @param {Event} event - Event to be tested. The propagation of this event will be blocked if it was emitted when
     *      the user pressed on Enter
     */
    module.AddressSearchBarController.prototype._handleKeyPressEvent = function(event) {
        // Block propagation of the event if the user pressed the Enter key
        var keyCode = event.keyCode || event.which;
        if (keyCode === 13) { 
            event.preventDefault();
            event.stopPropagation();
        }

        // Display the reset button if the contents of the input field are not empty
        this._displayClearButton();
    };

    module.AddressSearchBarController.prototype._displayClearButton = function() {
        // Display the reset button if the contents of the input field are not empty
        if ($('#am-address-search-field').val() !== "") {
            $('#am-address-clear-button').closest('.input-group-btn').removeClass('hidden');
        } else {
            $('#am-address-clear-button').closest('.input-group-btn').addClass('hidden');
        }
    };

    /**
     * Private function displaying an error message in case the address entered by the user does not exist or if the 
     * Google server returned an error message
     *
     * @param {jQuery} domElement - DOM element corresponding to the address search field
     * @param {String} title - Title of the error popup
     * @param {String} message - Message to be displayed in the error popup
     */
    module.AddressSearchBarController.prototype._displayErrorMessage = function(domElement, message) {
        am.utils.displayMessageNextToElement(domElement, 'error-message', { message: message }, 'bottom', {
            customClass: 'am-dropdown-error-message', 
            duration: 3000
        });
    };

    /**
     * Displays, in the address search bar, the address of the location whose coordinates are passed as input argument
     *
     * @param {L.LatLng} latLng - Coordinates of the point whose address is to be displayed in the search bar
     */
    module.AddressSearchBarController.prototype._displayAddressOfLocation = function(latLng) {
        var _this = this;
        this._oGeocoder.geocode({location: latLng}, function(results, status) { // caution: results can be null if status == 'OVER_QUERY_LIMIT'
            _this._view.getInputField().then(function(inputField) {
                var address;
                if (status == "OK" && results[0].types.indexOf('country') == -1) { // hack: in case a country was specified to restrict the search, Google returns this country if no matching address was found
                    address = results[0].formatted_address;
                } else if (status != 'OVER_QUERY_LIMIT') {
                    address = am.translator.translate("Unknown address");
                }
                am.eventManager.trigger('address_search_field_updated', address);
                inputField.value = address;

            });
        });
    };

    /**
     * Private function returning the correct options to be passed to the google.maps.places.Autocomplete and 
     * google.maps.Geocoder objects
     *
     * @param {String} [address] - Optional parameter specifying the searched address (if already known)
     * @param {Object} - Parameters to be passed to the Google objects
     */
    module.AddressSearchBarController.prototype._getGeocodingOptions = function(eType, address) {
        var options = {};
        if(eType === "Google") {
          if (address && typeof(address) == "string") {
            options.address = address;
          } else {
            options.types = ['address'];
          }
          if (this._config.searchBounds != undefined) {
            var searchBounds = this._config.searchBounds;
            options.bounds = new google.maps.LatLngBounds(
              new google.maps.LatLng(searchBounds.getSouth(), searchBounds.getWest()),
              new google.maps.LatLng(searchBounds.getNorth(), searchBounds.getEast())
            );
          }
          if (this._config.searchCountry != undefined) {
            options.componentRestrictions = {country: this._config.searchCountry};
          }
        }
        else if(eType === "Geoportail"){
          options.address = ( (address && typeof(address) === "string") ? address : "" );
        }
        return options;
    };

    return module;

})(am || {});


//=============================================
// AM.UTILS NAMESPACE
//=============================================
am.utils = (function(module) {

    // Use strict mode
    'use strict';

    /**
     * Function returning a new instance of class am.AddressSearchBarController
     *
     * @param {Object} mainController - Reference to the main controller of the application
     * @param {Object} config - Configuration object to be passed to the constructor of class 
     *      am.AddressSearchBarController
     * @return {am.AddressSearchBarController} - New instance of class am.AddressSearchBarController
     */
    module.addressSearchBarController = function(mainController, config) {
        var ctrl = new am.AddressSearchBarController();
        ctrl.init(mainController, config);
        return ctrl;
    };

    return module;

})(am.utils || {});